import React, { ReactText, useCallback, ReactNode } from 'react';
import {
  MenuItem,
  MenuItemProps,
  PopoverOrigin,
  Select,
  SelectProps,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { isString, startCase } from 'lodash';
import { colors } from 'spw-theme';
import { getIdentity } from 'spw-shared/utils';
import { Item } from '../../types';

const useStyles = makeStyles({
  select: {
    '& .MuiSelect-outlined': {
      fontSize: '14px',
      lineHeight: '20px',
      padding: '10px 12px',
    },
    '& .MuiFormLabel-root': {
      color: 'unset',
    },
    '& .MuiSelect-outlined[aria-expanded="true"] .MuiSelect-icon': {
      transform: 'rotate(180deg)',
    },
    '&.MuiOutlinedInput-root': {
      borderRadius: 0,
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: colors.white,
    },
  },
  paper: {
    '& .MuiMenuItem-root': {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  defaultChevron: {
    top: 'calc(50% - 9px)',
  },
  nullValueItem: {
    display: (props?: { displayEmptyValueOption?: boolean }) =>
      props.displayEmptyValueOption ? 'block' : 'none',
  },
  placeholder: {
    color: colors.mainGray,
  },
});

export const DefaultChevron: SelectProps['IconComponent'] = ({
  className,
  props,
}) => {
  const classes = useStyles({});

  return (
    <svg
      {...props}
      className={`${className ?? ''} ${classes.defaultChevron}`}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
      aria-hidden="true"
    >
      <g clipPath="url(#clip0_1365_1111)">
        <path
          d="M12.4425 6.4425L9 9.8775L5.5575 6.4425L4.5 7.5L9 12L13.5 7.5L12.4425 6.4425Z"
          fill="black"
        />
      </g>
      <defs>
        <clipPath id="clip0_1365_1111">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DisabledChevron: SelectProps['IconComponent'] = ({
  className,
  props,
}) => {
  const classes = useStyles({});

  return (
    <svg
      {...props}
      className={`${className ?? ''} ${classes.defaultChevron}`}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
      aria-hidden="true"
    >
      <g clipPath="url(#clip0_1365_1111)">
        <path
          d="M12.4425 6.4425L9 9.8775L5.5575 6.4425L4.5 7.5L9 12L13.5 7.5L12.4425 6.4425Z"
          fill={`${colors.mainGray}`}
        />
      </g>
      <defs>
        <clipPath id="clip0_1365_1111">
          <rect width="18" height="18" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

type Props<T extends Item | string> = Omit<
  SelectProps,
  'onChange' | 'value'
> & {
  items: T[];
  maxVisibleItems?: number;
  onChange: (newValue: T) => void;
  renderItem?: (item: T) => ReactText | JSX.Element | JSX.Element[];
  getItemIsDisabled?: (item: T) => boolean;
  getItemAriaLabel?: (item: T) => string;
  getItemTestId?: (item: T) => string;
  value: T | string;
  'data-testid'?: string;
  displayEmptyValueOption?: boolean;
};

const DEFAULT_CUSTOM_ANCHOR_ORIGIN = {
  horizontal: 'center',
  vertical: 'bottom',
} as PopoverOrigin;

const DEFAULT_CUSTOM_DISABLE_SCROLL_LOCK = false;

const NO_SELECTION_VALUE = '';
const BaseSelect = <T extends Item | string = string>({
  className,
  items,
  maxVisibleItems,
  onChange,
  placeholder,
  renderItem,
  value,
  getItemIsDisabled,
  getItemTestId,
  renderValue,
  getItemAriaLabel,
  displayEmptyValueOption = false,
  ...selectProps
}: Props<T>) => {
  const classes = useStyles({ displayEmptyValueOption });

  const anchorOrigin =
    selectProps?.MenuProps?.anchorOrigin ?? DEFAULT_CUSTOM_ANCHOR_ORIGIN;
  const disableScrollLock =
    selectProps?.MenuProps?.disableScrollLock ??
    DEFAULT_CUSTOM_DISABLE_SCROLL_LOCK;
  const paperStyle = {
    ...selectProps?.MenuProps?.PaperProps?.style,
  };

  if (maxVisibleItems) {
    paperStyle.height = `${16 + maxVisibleItems * 32}px`;
  }

  const handleChange = useCallback<SelectProps['onChange']>(
    (_, menuItem: ReactNode & { props: MenuItemProps }) => {
      const menuItemValue = menuItem.props.value as string;

      const newValue = isString(items[0])
        ? menuItemValue
        : items.find(
            (item) => String((item as Item).id) === String(menuItemValue)
          );

      onChange(newValue as T);
    },
    [items, onChange]
  );

  const chevron = selectProps?.disabled ? DisabledChevron : DefaultChevron;

  return (
    <Select
      {...selectProps}
      IconComponent={selectProps.IconComponent || chevron}
      className={`${className ?? ''} ${
        value === ' ' || !value ? classes.placeholder : ''
      } ${classes.select}`}
      onChange={handleChange}
      MenuProps={{
        ...selectProps?.MenuProps,
        anchorOrigin,
        disableScrollLock,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        'data-testid': `${selectProps['data-testid']}_menu` ?? undefined,
        PaperProps: {
          ...selectProps?.MenuProps?.PaperProps,
          style: paperStyle,
          className: `${selectProps?.MenuProps?.PaperProps?.className ?? ''} ${
            classes.paper
          }`,
        },
      }}
      value={
        isString(value) ? value : (value as Item)?.id ?? NO_SELECTION_VALUE
      }
      displayEmpty
      renderValue={
        placeholder
          ? (selected) => {
              if (selected) {
                if (renderValue) {
                  return renderValue(selected);
                }

                if (isString(selected) && !renderItem) {
                  return startCase(selected);
                }

                if (Array.isArray(selected)) {
                  return selected.join(', ');
                }

                const itemSelected = items.find(
                  (item) => (item as Item).id === selected
                );

                return itemSelected ? renderItem(itemSelected) : placeholder;
              }

              return placeholder;
            }
          : undefined
      }
    >
      <MenuItem
        value={NO_SELECTION_VALUE}
        disabled={!displayEmptyValueOption}
        className={classes.nullValueItem}
      >
        {placeholder}
      </MenuItem>
      {items.map((item) => (
        <MenuItem
          key={getIdentity(item)}
          value={getIdentity(item)}
          disabled={getItemIsDisabled?.(item)}
          data-testid={
            selectProps.name
              ? `${selectProps.name}_${
                  getItemTestId ? getItemTestId(item) : renderItem(item)
                }`
              : undefined
          }
          aria-label={getItemAriaLabel?.(item)}
        >
          {renderItem ? renderItem(item) : startCase(item as string)}
        </MenuItem>
      ))}
    </Select>
  );
};

export default BaseSelect;
